import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Heading, 
  SimpleGrid, 
  Text, 
  Spinner, 
  Center, 
  VStack, 
  Button,
  Flex,
  Select,
  Divider
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { eventService } from '../services/eventService';
import { Event, EventStatus } from '../types/types';
import { sportOf, SportKind } from '../utils/eventUtils';
import { VideoPlayer } from '../components/events/VideoPlayer';
import { EventStatusBadge } from '../components/events/EventStatusBadge';
import { format, parseISO } from 'date-fns';

export const FLSDashboardPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now());
  const [backgroundRefresh, setBackgroundRefresh] = useState<boolean>(false);
  const [visibleStreamsCount, setVisibleStreamsCount] = useState<number>(3);
  const [sportFilter, setSportFilter] = useState<'ALL' | SportKind>('ALL');
  const navigate = useNavigate();

  // Check if any event has DISABLED_BY_PROVIDER status
  const hasDisabledByProviderEvent = useMemo(() => {
    return events.some(event => event.status === EventStatus.DISABLED_BY_PROVIDER);
  }, [events]);

  // Fetch events using current time
  const fetchTodayEvents = async () => {
    try {
      // Only show loading spinner on initial load, not during background refreshes
      if (!backgroundRefresh) {
        setLoading(true);
      }
      
      // Use current time instead of start of day
      const currentTime = new Date(new Date().getTime() - 5 * 60 * 1000);
      const fetchedEvents = await eventService.getAllEvents(currentTime);
      setEvents(fetchedEvents);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
    } finally {
      setLoading(false);
      // After first successful load, set backgroundRefresh to true
      setBackgroundRefresh(true);
    }
  };

  // Set up auto-refresh with dynamic interval based on event status
  useEffect(() => {
    fetchTodayEvents();
    
    // Set refresh interval to 3 seconds if any event has DISABLED_BY_PROVIDER status, otherwise 30 seconds
    const refreshInterval = hasDisabledByProviderEvent ? 3000 : 30000;
    
    const intervalId = setInterval(() => {
      setRefreshTimestamp(Date.now());
      fetchTodayEvents();
    }, refreshInterval);
    
    return () => clearInterval(intervalId);
  }, [hasDisabledByProviderEvent]); // Re-create interval when hasDisabledByProviderEvent changes

  // Group events by video URL
  const eventsByVideoUrl = useMemo(() => {
    const groupedEvents: Record<string, Event[]> = {};
    
    // Only include events with video URLs, matching the selected sport
    events
      .filter(event => event.videoUrl && event.videoUrl.trim() !== '')
      .filter(event => sportFilter === 'ALL' || sportOf(event) === sportFilter)
      .forEach(event => {
        const videoUrl = event.videoUrl as string;
        if (!groupedEvents[videoUrl]) {
          groupedEvents[videoUrl] = [];
        }
        groupedEvents[videoUrl].push(event);
      });
    
    // Sort events within each group by start time
    Object.keys(groupedEvents).forEach(url => {
      groupedEvents[url].sort((a, b) => {
        const aTime = new Date(a.startTime).getTime();
        const bTime = new Date(b.startTime).getTime();
        return aTime - bTime; // Ascending order by start time
      });
    });
    
    return groupedEvents;
  }, [events, sportFilter]);

  // Sort video URLs by the earliest event start time in each group
  const sortedVideoUrls = useMemo(() => {
    return Object.entries(eventsByVideoUrl)
      .map(([url, events]) => ({
        url,
        earliestStartTime: events.length > 0 ? new Date(events[0].startTime).getTime() : Infinity
      }))
      .sort((a, b) => a.earliestStartTime - b.earliestStartTime)
      .map(item => item.url);
  }, [eventsByVideoUrl]);

  // Check if an event is currently live
  const isEventLive = (event: Event): boolean => {
    const now = new Date().getTime();
    const startTime = new Date(event.startTime).getTime();
    return now >= startTime && event.status === EventStatus.ACTIVE;
  };

  // Sort events for display under each video
  const getSortedEvents = (events: Event[]): Event[] => {
    // First, separate live and upcoming events
    const liveEvents = events.filter(isEventLive);
    const upcomingEvents = events.filter(event => !isEventLive(event));
    
    // Sort live events by start time (most recent first)
    liveEvents.sort((a, b) => {
      return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
    });
    
    // Sort upcoming events by start time (earliest first)
    upcomingEvents.sort((a, b) => {
      return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
    });
    
    // Return live events followed by upcoming events
    return [...liveEvents, ...upcomingEvents];
  };

  // Render a single event item
  const renderEventItem = (event: Event) => {
    const isLive = isEventLive(event);
    
    return (
      <Box 
        key={event.id} 
        p={3} 
        borderWidth="1px" 
        borderRadius="md" 
        bg={isLive ? "green.50" : "white"}
        borderLeftWidth={isLive ? "4px" : "1px"}
        borderLeftColor={isLive ? "green.400" : "gray.200"}
        mb={2}
      >
        <Text 
          fontWeight="bold" 
          cursor="pointer" 
          onClick={() => navigate(`/events/${event.id}`)}
          _hover={{ color: "blue.500", textDecoration: "underline" }}
        >
          {event.firstCompetitor.name} vs {event.secondCompetitor.name}
          {isLive && <Text as="span" color="green.500" ml={2}>LIVE</Text>}
        </Text>
        <Text fontSize="sm" color="gray.600">ID: {event.id}</Text>
        <Text fontSize="sm">
          Start: {format(parseISO(event.startTime), 'HH:mm')}
        </Text>
        <EventStatusBadge status={event.status} />
        {/*<Button*/}
        {/*  size="sm"*/}
        {/*  colorScheme="blue"*/}
        {/*  mt={2}*/}
        {/*  onClick={() => navigate(`/events/${event.id}`)}*/}
        {/*>*/}
        {/*  Details*/}
        {/*</Button>*/}
      </Box>
    );
  };

  // Render a video section with its events
  const renderVideoSection = (videoUrl: string, events: Event[]) => {
    // Only show video for events with ACTIVE status
    const hasActiveEvent = events.some(event => event.status === EventStatus.ACTIVE);
    const sortedEvents = getSortedEvents(events);
    
    return (
      <Box 
        key={videoUrl} 
        borderWidth="1px" 
        borderRadius="lg" 
        overflow="hidden" 
        p={4} 
        bg="white" 
        shadow="md"
      >
        {hasActiveEvent && <VideoPlayer videoUrl={videoUrl} title="Live Stream" />}
        {!hasActiveEvent && (
          <Box p={4} bg="gray.100" borderRadius="md" mb={4}>
            <Text align="center" color="gray.500">
              Video playback disabled - No active events
            </Text>
          </Box>
        )}
        
        <Heading size="md" mb={3}>Events</Heading>
        <VStack spacing={2} align="stretch">
          {sortedEvents.map(renderEventItem)}
        </VStack>
      </Box>
    );
  };

  return (
    <Box>
      <Heading mb={6}>FLS Dashboard</Heading>
      <Flex justify="space-between" align="center" mb={4}>
        <Text>
          Last updated: {new Date(refreshTimestamp).toLocaleTimeString()}
        </Text>
        <Flex align="center" gap={4}>
          <Flex align="center">
            <Text mr={2} fontSize="sm">Sport:</Text>
            <Select
              value={sportFilter}
              onChange={(e) => setSportFilter(e.target.value as 'ALL' | SportKind)}
              size="sm"
              width="150px"
            >
              <option value="ALL">All sports</option>
              <option value="CRICKET">Cricket</option>
              <option value="TABLE_TENNIS">Table Tennis</option>
              <option value="TENNIS">Tennis</option>
            </Select>
          </Flex>
          <Flex align="center">
            <Text mr={2} fontSize="sm">Visible streams:</Text>
            <Select
              value={visibleStreamsCount}
              onChange={(e) => setVisibleStreamsCount(parseInt(e.target.value))}
              size="sm"
              width="80px"
            >
              {[1, 2, 3, 4, 5].map(num => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>
      </Flex>
      
      {loading && !backgroundRefresh && (
        <Center py={10}>
          <Spinner size="xl" />
        </Center>
      )}
      
      {error && (
        <Center py={10}>
          <Text color="red.500">{error}</Text>
        </Center>
      )}
      
      {!loading && !error && Object.keys(eventsByVideoUrl).length === 0 && (
        <Center py={10}>
          <Text>No events with video URLs found for today.</Text>
        </Center>
      )}
      
      {/* Visible streams section */}
      {sortedVideoUrls.length > 0 && (
        <>
          <SimpleGrid columns={{ base: 1, lg: Math.min(2, Math.min(visibleStreamsCount, sortedVideoUrls.length)), xl: Math.min(3, Math.min(visibleStreamsCount, sortedVideoUrls.length)) }} spacing={6} mb={6}>
            {sortedVideoUrls.slice(0, visibleStreamsCount).map(videoUrl => 
              renderVideoSection(videoUrl, eventsByVideoUrl[videoUrl])
            )}
          </SimpleGrid>
          
          {/* Divider and remaining streams */}
          {sortedVideoUrls.length > visibleStreamsCount && (
            <>
              <Divider my={8} />
              <Heading size="md" mb={4}>Additional Streams</Heading>
              <SimpleGrid columns={{ base: 1, lg: Math.min(2, sortedVideoUrls.length - visibleStreamsCount), xl: Math.min(3, sortedVideoUrls.length - visibleStreamsCount) }} spacing={6}>
                {sortedVideoUrls.slice(visibleStreamsCount).map(videoUrl => 
                  renderVideoSection(videoUrl, eventsByVideoUrl[videoUrl])
                )}
              </SimpleGrid>
            </>
          )}
        </>
      )}
    </Box>
  );
};
